import { proStudioUrqlGql } from '@pypestream/api-services';
import { runInAction } from 'mobx';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { tableStore } from '../stores/table-store';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import type { SolutionUserPresenceItem } from '../xstate/prostudio.xstate.types';
import {
  hasOtherActiveUserOnPage,
  shouldApplyCoworkerPush,
  shouldOpenCoworkerSubscription,
} from './coworker-subscription-gate';

/** Stable string for "who has which cell focused" so we run validation only when focus changes. */
function getFocusSignature(
  presences: SolutionUserPresenceItem[] | undefined,
  pathname: string,
  currentUserId: string
): string {
  if (!presences?.length) return '';
  const parts = presences
    .filter(
      (p) =>
        String(p.userId ?? p.user?.id ?? '') !== currentUserId &&
        p.isActive &&
        p.pagePath === pathname &&
        p.pageParams?.focusedCell
    )
    .map(
      (p) =>
        `${p.userId ?? p.user?.id ?? ''}:${p.pageParams!.focusedCell!.rowId}:${p.pageParams!.focusedCell!.columnId}`
    )
    .sort();
  return parts.join('|');
}

/**
 * Subscribes to solution version updates only when another user has an active
 * focus. Applies data without validation unless focus changed (or no focus now).
 */
export function useCoworkerFocusSubscription() {
  const { pathname } = useLocation();

  const solutionUserPresences = useProStudioCtxSelector(
    (ctx) => ctx.solutionUserPresences
  );
  const userInfo = useProStudioCtxSelector((ctx) => ctx.userInfo);

  const { solutionVersionId } = tableStore;

  const currentUserId = String(userInfo?.id ?? '');
  // PSTUDIO-3929 (b): subscribe whenever another active user is on this same
  // table page — NOT only while they hold a focused cell — so a teammate's
  // newly added node propagates without the receiver reloading. The focused-
  // cell signal still drives WHEN validation re-runs (getFocusSignature below).
  const hasOtherActiveUser = hasOtherActiveUserOnPage(
    solutionUserPresences,
    pathname,
    currentUserId
  );

  // Require a resolved currentUserId: before userInfo loads it is '', which
  // would make hasOtherActiveUserOnPage treat every presence as "someone else"
  // and open the subscription a beat early during bootstrap.
  const shouldSubscribe = shouldOpenCoworkerSubscription({
    solutionVersionId,
    currentUserId,
    hasOtherActiveUser,
    liveEditingSource: tableStore.liveEditingSource,
  });
  const prevShouldSubscribe = useRef(false);

  const currentFocusSignatureRef = useRef('');
  const lastValidationFocusSignatureRef = useRef('');
  currentFocusSignatureRef.current = getFocusSignature(
    solutionUserPresences,
    pathname,
    currentUserId
  );

  useEffect(() => {
    if (!shouldSubscribe) {
      if (prevShouldSubscribe.current) {
        tableStore.cleanupCoworkerFocusSubscription();
        runInAction(() => {
          tableStore.validateAndSync();
        });
      }
      prevShouldSubscribe.current = false;
      return;
    }

    prevShouldSubscribe.current = true;
    lastValidationFocusSignatureRef.current = currentFocusSignatureRef.current;

    try {
      const { subscribe } = proStudioUrqlGql.solutionVersionSubscription({
        solutionVersionId: solutionVersionId!,
      });

      const { unsubscribe } = subscribe((subData) => {
        try {
          if (subData.error) return;
          // PSTUDIO-3953: this callback keeps arriving while the store moves on
          // (a publish bump, a solution switch); the effect's cleanup races the
          // messages already queued in urql. Everything below belongs to the
          // version this subscription was opened for, so stop here when that is
          // no longer the version the store holds.
          if (
            !shouldApplyCoworkerPush({
              subscribedFor: solutionVersionId,
              storeVersion: tableStore.solutionVersionId,
              liveEditingSource: tableStore.liveEditingSource,
            })
          ) {
            return;
          }
          const versionData =
            subData.data?.tenancy_solution_versions_ps_by_pk ?? null;
          if (!versionData) {
            // PSTUDIO-3671: the subscribed (already-loaded) solution version was
            // deleted — a null here is not "still loading". Eject via the machine.
            // Guarded by the identity check above: without it a late null for a
            // version the user already left ejects them out of the LIVE one.
            sendProStudioEvent({ type: 'solutionNotFound' });
            return;
          }

          const focusChanged =
            currentFocusSignatureRef.current !==
            lastValidationFocusSignatureRef.current;

          if (focusChanged) {
            lastValidationFocusSignatureRef.current =
              currentFocusSignatureRef.current;
          }

          runInAction(async () => {
            // PSTUDIO-3625: apply the coworker's edit INCREMENTALLY, the same
            // write-minimal way the cross-tab (same-user) reload does.
            // processVersionData does `this.rows.replace(...)`, which flushes
            // the whole observable array — the top-level <Table/> observer
            // re-renders every row and react-virtual resets scroll/selection,
            // producing the "reload"/flicker on every subscription push.
            // mergeVersionData diffs incoming (already-normalized) rows against
            // live rows by id and mutates only what changed; a foreign-origin
            // snapshot is safe because the merge re-runs the same group
            // reconcile/collapse + validation passes as processVersionData.
            // processVersionData stays for genuine version-switch/initial load.
            // PSTUDIO-3930 (Option A): this is the TEAMMATE live-merge path — a
            // change originating from another user. If it actually mutates the
            // table, tableStore invalidates the undo history (clean slate) so a
            // stale command can't overwrite the teammate's edit or desync from
            // the server. The same-user cross-tab reload (reload-nodes.ts)
            // deliberately does NOT pass this flag.
            // PSTUDIO-3953: a coworker push is applied under the identity it
            // was received for. The subscription is opened per solution
            // version, but its callbacks keep arriving while the user switches
            // solution or a publish bumps the draft, and this path's "Removes"
            // phase would then delete every live row.
            await tableStore.mergeVersionData(versionData, {
              skipValidation: !focusChanged,
              resetHistoryOnRemoteChange: true,
              forSolutionVersionId: solutionVersionId,
            });
          });
        } catch (_err) {
          // ignore per-update errors
        }
      });

      tableStore.coworkerFocusSubscriptionUnsubscribe = unsubscribe;
    } catch (_err) {
      prevShouldSubscribe.current = false;
    }

    return () => {
      tableStore.cleanupCoworkerFocusSubscription();
      prevShouldSubscribe.current = false;
    };
  }, [shouldSubscribe, solutionVersionId]);
}
